import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

interface CarbonImpact {
  carbonSaved: number;
  materialsDiverted: number;
  greenRating: number;
  impactDescription: string;
  calculationDetails: {
    materialType: string;
    condition: string;
    estimatedWeight: number;
    carbonFactor: number;
    diversionFactor: number;
  };
}

interface ListingWithImpact {
  _id: string;
  title: string;
  description: string;
  price: number;
  condition: string;
  category: string;
  images: string[];
  status: string;
  createdAt: string;
  carbonImpact: CarbonImpact;
}

interface Achievement {
  title: string;
  description: string;
  icon: string;
  level: 'bronze' | 'silver' | 'gold';
}

interface DashboardData {
  summary: {
    totalListings: number;
    totalItems: number;
    totalCarbonSaved: number;
    totalMaterialsDiverted: number;
    averageGreenRating: number;
  };
  listings: ListingWithImpact[];
  items: any[];
  aggregateImpact: {
    totalCarbonSaved: number;
    totalMaterialsDiverted: number;
    averageGreenRating: number;
    listingCount: number;
    impactByCategory: Record<string, { carbonSaved: number; materialsDiverted: number; count: number }>;
  };
  monthlyTrends: Array<{
    month: string;
    carbonSaved: number;
    materialsDiverted: number;
    listingCount: number;
  }>;
  topCategories: Array<{
    category: string;
    carbonSaved: number;
    materialsDiverted: number;
    count: number;
    averageRating: number;
  }>;
  achievements: Achievement[];
  generatedAt: string;
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Helper function to safely format numbers
  const safeToFixed = (value: number | null | undefined, decimals: number = 1): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.0';
    }
    return value.toFixed(decimals);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: DashboardData }>('/dashboard/environmental-impact');
      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const exportToPDF = async () => {
    try {
      setExportingPDF(true);
      const response = await fetch('/api/dashboard/environmental-impact/pdf', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `environmental-impact-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={`text-2xl ${i <= rating ? 'text-yellow-400 dark:text-yellow-300' : 'text-gray-300 dark:text-gray-600'}`}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  const getImpactColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-700';
    if (rating >= 3) return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700';
    if (rating >= 2) return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-700';
    return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-800/50 dark:border-gray-600';
  };

  const getAchievementColor = (level: string) => {
    switch (level) {
      case 'gold': return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-700';
      case 'silver': return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-800/50 dark:border-gray-600';
      case 'bronze': return 'text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-900/20 dark:border-orange-700';
      default: return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your environmental impact dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No Data Available</h2>
          <p className="text-gray-600 dark:text-gray-400">Create some listings to see your environmental impact!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Environmental Impact Dashboard
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Track your contribution to sustainability through the Deconstructed marketplace
              </p>
            </div>
            <button
              onClick={exportToPDF}
              disabled={exportingPDF}
              className="btn-primary flex items-center gap-2"
            >
              {exportingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export PDF Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Green Guardian Achievement Banner */}
        {(dashboardData.summary.totalCarbonSaved || 0) >= 100 && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border-2 border-green-200 dark:border-green-700 p-8 text-center relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-100/50 to-transparent dark:from-green-800/20 dark:to-transparent"></div>
              <div className="relative z-10">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-green-500 dark:bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-3xl">🌱</span>
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-green-800 dark:text-green-200 mb-2">
                  Green Guardian
                </h2>
                <p className="text-lg text-green-700 dark:text-green-300 mb-4">
                  Congratulations! You've saved over {Math.floor(dashboardData.summary.totalCarbonSaved || 0)}kg of CO₂ emissions
                </p>
                <div className="flex justify-center items-center gap-4 text-sm text-green-600 dark:text-green-400">
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Environmental Champion</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    <span>Sustainability Leader</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Getting Started Banner for New Users */}
        {dashboardData.summary.totalListings < 5 && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border-2 border-blue-200 dark:border-blue-700 p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-transparent dark:from-blue-800/20 dark:to-transparent"></div>
              <div className="relative z-10 flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🚀</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200 mb-1">
                    Getting Started
                  </h3>
                  <p className="text-blue-700 dark:text-blue-300 mb-2">
                    Great job creating your first sustainable listings! Keep adding more items to maximize your environmental impact.
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200">
                      ✅ First listing created
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200">
                      🎯 Add {5 - dashboardData.summary.totalListings} more for Green Badge
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overall Green Rating */}
        <div className="mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Your Overall Green Rating
              </h2>
              <div className="flex justify-center items-center mb-4">
                {renderStars(Math.floor(dashboardData.summary.averageGreenRating || 0))}
                <span className="ml-3 text-2xl font-bold text-gray-900 dark:text-white">
                  {safeToFixed(dashboardData.summary.averageGreenRating, 1)}/5.0
                </span>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {(dashboardData.summary.averageGreenRating || 0) >= 4 
                  ? "Outstanding environmental impact! You're making a significant difference in sustainability."
                  : (dashboardData.summary.averageGreenRating || 0) >= 3
                  ? "Great environmental contribution! Your efforts are helping build a more sustainable future."
                  : (dashboardData.summary.averageGreenRating || 0) >= 2
                  ? "Good sustainable practices! Keep up the great work in reducing waste."
                  : "Every step towards sustainability counts! Continue your journey to make an even greater impact."
                }
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Listings</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{dashboardData.summary.totalListings}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Carbon Saved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(dashboardData.summary.totalCarbonSaved, 1)} kg</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">CO₂ equivalent</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Waste Diverted</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(dashboardData.summary.totalMaterialsDiverted, 1)} kg</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">From landfill</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Rating</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(dashboardData.summary.averageGreenRating, 1)}/5</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Green stars</p>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements */}
        {dashboardData.achievements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Your Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.achievements.map((achievement, index) => (
                <div
                  key={index}
                  className={`rounded-lg border-2 p-4 ${getAchievementColor(achievement.level)}`}
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-3">{achievement.icon}</span>
                    <h3 className="font-bold text-lg">{achievement.title}</h3>
                  </div>
                  <p className="text-sm">{achievement.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Impact by Category</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Carbon Saved
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Waste Diverted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Listings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {dashboardData.topCategories.map((category, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {category.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {safeToFixed(category.carbonSaved, 1)} kg CO₂
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {safeToFixed(category.materialsDiverted, 1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {category.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top Performing Listings */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Your Top Environmental Impact Listings</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dashboardData.listings
              .sort((a, b) => b.carbonImpact.carbonSaved - a.carbonImpact.carbonSaved)
              .slice(0, 6)
              .map((listing) => (
                <div
                  key={listing._id}
                  className={`rounded-lg border-2 p-6 ${getImpactColor(listing.carbonImpact.greenRating)}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2">{listing.title}</h3>
                      <div className="flex items-center mb-2">
                        {renderStars(listing.carbonImpact.greenRating)}
                        <span className="ml-2 text-sm font-medium">
                          {listing.carbonImpact.greenRating}/5 Green Rating
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Price</p>
                      <p className="font-bold">${listing.price} NZD</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Carbon Saved</p>
                      <p className="font-semibold">{safeToFixed(listing.carbonImpact.carbonSaved, 1)} kg CO₂</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Waste Diverted</p>
                      <p className="font-semibold">{safeToFixed(listing.carbonImpact.materialsDiverted, 1)} kg</p>
                    </div>
                  </div>
                  
                  <p className="text-sm mb-3">{listing.carbonImpact.impactDescription}</p>
                  
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                    <span>{listing.category} • {listing.condition}</span>
                    <span>{new Date(listing.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <p>Report generated on {new Date(dashboardData.generatedAt).toLocaleString()}</p>
          <p className="mt-1">Keep up the great work contributing to a more sustainable future! 🌱</p>
        </div>
      </div>
    </div>
  );
};
